/**
 * Passkeys (WebAuthn) for console sign-in (P4-07). Server: /api/auth/passkeys.
 *
 * The server sends PublicKeyCredential*OptionsJSON (binary fields as
 * base64url); the browser API wants ArrayBuffers and returns a credential that
 * is sent back as JSON. Browsers with WebAuthn Level 3 do both conversions
 * natively (PublicKeyCredential.parse*OptionsFromJSON, credential.toJSON());
 * the manual fallback below covers the rest. No third-party library.
 */
import { api } from '@/services/api';

export interface PasskeySummary {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
  device_type: string | null;
  backed_up: boolean;
  transports: string[];
}

export interface PasskeySignInResult {
  token: string;
  refresh_token?: string;
  user: {
    id: string;
    email: string;
    name: string;
    phone?: string;
    job_title?: string;
    avatar_url?: string;
    onboarding_completed_at?: string | null;
    is_platform_admin?: boolean;
  };
}

type Json = Record<string, unknown>;

export function base64urlToBuffer(value: string): ArrayBuffer {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64url(value: ArrayBuffer | ArrayBufferView): string {
  // ArrayBuffer.isView, not instanceof: buffers can come from another realm
  // (an iframe, the test DOM), where instanceof ArrayBuffer is false.
  const bytes = ArrayBuffer.isView(value)
    ? new Uint8Array(value.buffer, value.byteOffset, value.byteLength)
    : new Uint8Array(value as ArrayBuffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Whether this browser can do WebAuthn at all (a secure context with PublicKeyCredential). */
export function browserSupportsPasskeys(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext !== false &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials
  );
}

type PKCStatic = typeof PublicKeyCredential & {
  parseCreationOptionsFromJSON?: (json: Json) => PublicKeyCredentialCreationOptions;
  parseRequestOptionsFromJSON?: (json: Json) => PublicKeyCredentialRequestOptions;
};

function descriptors(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return list.map((d: Json) => ({
    type: 'public-key',
    id: base64urlToBuffer(String(d.id)),
    ...(Array.isArray(d.transports) ? { transports: d.transports as AuthenticatorTransport[] } : {}),
  }));
}

export function toCreationOptions(json: Json): PublicKeyCredentialCreationOptions {
  const pkc = window.PublicKeyCredential as PKCStatic;
  if (typeof pkc.parseCreationOptionsFromJSON === 'function') return pkc.parseCreationOptionsFromJSON(json);
  const user = json.user as Json;
  return {
    ...(json as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64urlToBuffer(String(json.challenge)),
    user: { ...(user as unknown as PublicKeyCredentialUserEntity), id: base64urlToBuffer(String(user.id)) },
    excludeCredentials: descriptors(json.excludeCredentials),
  };
}

export function toRequestOptions(json: Json): PublicKeyCredentialRequestOptions {
  const pkc = window.PublicKeyCredential as PKCStatic;
  if (typeof pkc.parseRequestOptionsFromJSON === 'function') return pkc.parseRequestOptionsFromJSON(json);
  return {
    ...(json as unknown as PublicKeyCredentialRequestOptions),
    challenge: base64urlToBuffer(String(json.challenge)),
    allowCredentials: descriptors(json.allowCredentials),
  };
}

/** Serialise a created or asserted credential to the JSON the server validates. */
export function credentialToJSON(credential: PublicKeyCredential): Json {
  const withJson = credential as PublicKeyCredential & { toJSON?: () => Json };
  if (typeof withJson.toJSON === 'function') return withJson.toJSON();
  const r = credential.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const response: Json = { clientDataJSON: bufferToBase64url(r.clientDataJSON) };
  if ('attestationObject' in r && r.attestationObject) {
    response.attestationObject = bufferToBase64url(r.attestationObject);
    if (typeof r.getTransports === 'function') response.transports = r.getTransports();
  }
  if ('authenticatorData' in r && r.authenticatorData) {
    response.authenticatorData = bufferToBase64url(r.authenticatorData);
    response.signature = bufferToBase64url(r.signature);
    if (r.userHandle) response.userHandle = bufferToBase64url(r.userHandle);
  }
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    ...(credential.authenticatorAttachment ? { authenticatorAttachment: credential.authenticatorAttachment } : {}),
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    response,
  };
}

/** Whether the server can run passkey ceremonies (library installed, RP configured). */
export async function fetchPasskeyAvailability(): Promise<boolean> {
  try {
    const res = await api.get<{ available: boolean }>('/auth/passkeys/status');
    return res.available === true;
  } catch {
    return false;
  }
}

/** Thrown when the person dismissed the browser's passkey prompt: not an error to show loudly. */
export class PasskeyCancelledError extends Error {
  constructor() {
    super('Passkey request was cancelled');
    this.name = 'PasskeyCancelledError';
  }
}

function rethrowBrowserError(err: unknown): never {
  const name = (err as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') throw new PasskeyCancelledError();
  throw err;
}

/** Add a passkey to the signed-in account. */
export async function registerPasskey(name?: string): Promise<PasskeySummary> {
  const started = await api.post<{ ceremony_id: string; options: Json }>('/auth/passkeys/registration/options');
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.create({ publicKey: toCreationOptions(started.options) });
  } catch (err) {
    rethrowBrowserError(err);
  }
  if (!credential) throw new PasskeyCancelledError();
  const res = await api.post<{ data: PasskeySummary }>('/auth/passkeys/registration/verify', {
    ceremony_id: started.ceremony_id,
    response: credentialToJSON(credential as PublicKeyCredential),
    ...(name && name.trim() ? { name: name.trim() } : {}),
  });
  return res.data;
}

/** Usernameless sign-in: the browser offers the passkeys it holds for this site. */
export async function signInWithPasskey(): Promise<PasskeySignInResult> {
  const started = await api.post<{ ceremony_id: string; options: Json }>('/auth/passkeys/authentication/options');
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.get({ publicKey: toRequestOptions(started.options) });
  } catch (err) {
    rethrowBrowserError(err);
  }
  if (!credential) throw new PasskeyCancelledError();
  return api.post<PasskeySignInResult>('/auth/passkeys/authentication/verify', {
    ceremony_id: started.ceremony_id,
    response: credentialToJSON(credential as PublicKeyCredential),
  });
}
